import React, { useEffect, useId, useRef, useState } from 'react';

/**
 * Mixed exercise inline note: highlighted clickable text with a tooltip.
 * Body syntax: `…anchor [note:anchor/tooltip]…` — the marker is stripped;
 * the matching preceding text becomes this highlight.
 */
export default function MixedNoteHighlight({ text, tooltip }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const tipId = useId();
    const label = String(text || '');
    const tip = String(tooltip || '').trim();

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    if (!label) return null;

    return (
        <span
            ref={wrapRef}
            className="ex-mixed-note-wrap"
            style={{ position: 'relative', display: 'inline', verticalAlign: 'baseline' }}
        >
            <button
                type="button"
                className="ex-mixed-note"
                aria-expanded={open}
                aria-describedby={open && tip ? tipId : undefined}
                title={tip || undefined}
                onClick={() => tip && setOpen((v) => !v)}
                style={{
                    display: 'inline',
                    margin: 0,
                    padding: '0 0.12em',
                    border: 'none',
                    borderRadius: 3,
                    background: 'rgba(251, 191, 36, 0.28)',
                    boxShadow: 'inset 0 -1.5px 0 0 #d97706',
                    color: 'inherit',
                    font: 'inherit',
                    fontWeight: 650,
                    lineHeight: 'inherit',
                    cursor: tip ? 'pointer' : 'default',
                    verticalAlign: 'baseline',
                }}
            >
                {label}
            </button>
            {open && tip ? (
                <span
                    id={tipId}
                    role="tooltip"
                    className="ex-mixed-note-tip"
                    style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 'calc(100% + 8px)',
                        transform: 'translateX(-50%)',
                        zIndex: 40,
                        // width:max-content — don't shrink to the highlight's narrow box
                        width: 'max-content',
                        maxWidth: 'min(280px, 90vw)',
                        padding: '8px 12px',
                        borderRadius: 10,
                        background: '#0f172a',
                        color: '#f8fafc',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        textAlign: 'center',
                        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.28)',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        pointerEvents: 'none',
                    }}
                >
                    {tip}
                    <span
                        aria-hidden
                        style={{
                            position: 'absolute',
                            left: '50%',
                            bottom: -6,
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '6px solid #0f172a',
                        }}
                    />
                </span>
            ) : null}
        </span>
    );
}
